import { styled } from '@linaria/react'
import React, { type FC, type ReactNode, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { useNavigate } from 'react-router'
import { boxCreateSuccessPagePath } from '../pages/BoxCreateSuccessPage'
import { colors } from '../styles/colors'
import { ButtonRegular } from '../ui/Button'
import { Typography } from '../ui/Typography'
import { ActionRejectedDialog } from './ActionRejectedDialog'
import { DialogContainer } from './common.styles'
import {
  trackBoxCreationStarted,
  trackBoxCreationSuccess,
  trackBoxCreationFailed,
  extractErrorMessage,
} from '../utils/amplitude'

const DetailsItemComponent = ({
  label,
  value,
}: {
  label: ReactNode
  value: ReactNode
}) => {
  return (
    <DetailsItem>
      <dt>
        <Typography fontSize={14} fontWeight={400} color={colors.grey700}>
          {label}
        </Typography>
      </dt>

      <dd>
        <Typography fontSize={14} fontWeight={400} color={colors.grey700}>
          {value}
        </Typography>
      </dd>
    </DetailsItem>
  )
}

export const CreateBoxConfirmDialog: FC<{
  onConfirm: () => Promise<void>
  data: {
    commission: string
    earnings: string
  }
}> = ({ onConfirm, data }) => {
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false)
  const [isError, setIsError] = useState(false)

  const handleConfirm = async () => {
    setIsLoading(true)
    trackBoxCreationStarted()
    try {
      await onConfirm()
      trackBoxCreationSuccess()
      setIsError(false)
      navigate(boxCreateSuccessPagePath)
    } catch (error) {
      console.error(error)
      trackBoxCreationFailed(extractErrorMessage(error))
      setIsError(true)
    } finally {
      setIsLoading(false)
    }
  }

  if (isError) {
    return (
      <ActionRejectedDialog isLoading={isLoading} onRetry={handleConfirm} />
    )
  }

  return (
    <DialogContainer>
      <TitleContainer>
        <Typography fontSize={20} fontWeight={600} color={colors.secondary}>
          <FormattedMessage
            id="dialog.createBoxConfirm.title"
            defaultMessage="Подтвердите создание нового бокса"
          />
        </Typography>
      </TitleContainer>

      <DetailsContainer>
        {[
          {
            id: 'commission',
            key: (
              <FormattedMessage
                id="dialog.createBoxConfirm.commission"
                defaultMessage="Комиссия"
              />
            ),
            value: data.commission,
          },

          {
            id: 'earnings',
            key: (
              <FormattedMessage
                id="dialog.createBoxConfirm.earnings"
                defaultMessage="Ваш заработок с бокса"
              />
            ),
            value: data.earnings,
          },
        ].map(({ key, value, id }) => (
          <DetailsItemComponent key={id} label={key} value={value} />
        ))}
      </DetailsContainer>

      <ButtonsContainer>
        <ConfirmButton
          loading={isLoading}
          onClick={handleConfirm}
          textColor={colors.white}
          textWeight={700}
        >
          <FormattedMessage
            id="dialog.createBoxConfirm.confirm"
            defaultMessage="Подтвердить"
          />
        </ConfirmButton>
      </ButtonsContainer>
    </DialogContainer>
  )
}

export const createBoxConfirmDialogPath = '/create-box-confirm-dialog'

const TitleContainer = styled.div`
  text-align: center;
  max-width: 280px;
`

const DetailsContainer = styled.dl`
  width: 100%;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 16px;
`

const DetailsItem = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: 16px;

  dt {
    margin: 0;
  }

  dd {
    margin: 0;
  }
`

const ButtonsContainer = styled.div`
  width: 100%;
  display: grid;
  gap: 12px;
  margin-top: 32px;
`

const ConfirmButton = styled(ButtonRegular)`
  width: 100%;
  background: ${colors.secondary};
  color: ${colors.white};
`
